'use client';

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Session } from '@ory/client';
import axios from 'axios';
import { SessionCustomerResponse } from '@/app/api/auth/session-and-customer/route';
import CrystallizeCustomerModel from '@/models/crystallize-customer-model';
import { CRYSTALLIZE_CUSTOMER_META_INITIAL_EMAIL_HASHED } from '@/integrations/crystallize/types/CustomerMetadata';
import { DataLayerHelper } from '@/utils/data-layer-helper';
import { IAuthContextProps } from './types/IAuthContextProps';

const UserContext = createContext<IAuthContextProps>({} as IAuthContextProps);

export function UserProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [customer, setCustomer] = useState<CrystallizeCustomerModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const userIdPushed = useRef(false);

  const fetchSessionAndCustomer = useCallback(async (bypassCache: boolean) => {
    if (!bypassCache && session && customer) {
      return;
    }
    setLoading(true);
    try {
      // Timeout bounds a hung upstream (Ory/PIM): a never-settling request would keep
      // isInitialized false and hide the club signup form for every visitor.
      const response = await axios.get<SessionCustomerResponse>('/api/auth/session-and-customer', {
        timeout: 10_000,
      });
      if (response.data.customer && response.data.session) {
        setSession(response.data.session);
        const customerModel = new CrystallizeCustomerModel(response.data.customer);
        setCustomer(customerModel);

        if (!userIdPushed.current) {
          userIdPushed.current = true;

          const hashedEmail =
            customerModel.getMetaField(CRYSTALLIZE_CUSTOMER_META_INITIAL_EMAIL_HASHED) ?? undefined;

          DataLayerHelper.pushUserId(hashedEmail as string);

          // GA4: Push login event only once per browser session (not on every page load)
          const loginTrackedKey = 'login_tracked';
          if (!sessionStorage.getItem(loginTrackedKey)) {
            sessionStorage.setItem(loginTrackedKey, 'true');
            DataLayerHelper.pushLogin('ory', hashedEmail);
          }
        }
      }
    } catch (error) {
      // Keep the last known session/customer: a transient failure here must not
      // flip the whole UI to "guest" — server-side guards still enforce real auth,
      // and a 200 response without a session already leaves state untouched.
      console.error('Failed to fetch session and customer:', error);
    } finally {
      setLoading(false);
      // Distinguishes "session check not yet run" from "checked, user is a guest".
      setIsInitialized(true);
    }
  }, []);

  const getSessionAndCustomer = useCallback(
    () => fetchSessionAndCustomer(false),
    [fetchSessionAndCustomer],
  );

  const refreshSessionAndCustomer = useCallback(
    () => fetchSessionAndCustomer(true),
    [fetchSessionAndCustomer],
  );

  const clearSessionAndCustomer = useCallback(() => {
    setSession(null);
    setCustomer(null);
  }, [setSession, setCustomer]);

  useEffect(() => {
    getSessionAndCustomer();
  }, [getSessionAndCustomer]);

  const contextValue = useMemo(
    () => ({
      session,
      customer,
      clearSessionAndCustomer,
      refreshSessionAndCustomer,
      loading,
      isInitialized,
      isLoggedIn: !!session?.active,
      isClubMember: !!customer?.isClubMember(),
    }),
    [session, customer, clearSessionAndCustomer, refreshSessionAndCustomer, loading, isInitialized],
  );

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
}

export default function useUser() {
  return useContext(UserContext);
}
